import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import Badge from "@/components/base/Badge";
import Eyebrow from "@/components/base/Eyebrow";
import DetailModal from "@/components/base/DetailModal";
import { clinicalServices, type DetailOption } from "@/mocks/site";
import { buildWhatsAppLink } from "@/utils/links";

type Tone = "gold" | "rose" | "lilac" | "clinic" | "beige" | "terracotta";

type ToneStyle = {
  bar: string;
  chip: Tone;
  halo: string;
  shadow: string;
};

const toneStyles: Record<Tone, ToneStyle> = {
  gold: { bar: "bar-gold", chip: "gold", halo: "glow-gold", shadow: "hover:shadow-gold-soft" },
  rose: { bar: "bar-rose", chip: "rose", halo: "glow-rose", shadow: "hover:shadow-rose-soft" },
  lilac: { bar: "bar-lilac", chip: "lilac", halo: "glow-lilac", shadow: "hover:shadow-lilac-soft" },
  clinic: { bar: "bar-clinic", chip: "clinic", halo: "glow-clinic", shadow: "hover:shadow-clinic-soft" },
  beige: { bar: "bar-beige", chip: "beige", halo: "glow-beige", shadow: "hover:shadow-beige-soft" },
  terracotta: {
    bar: "bar-terracotta",
    chip: "terracotta",
    halo: "glow-terracotta",
    shadow: "hover:shadow-terracotta-soft",
  },
};

const categoryTone: Record<string, Tone> = {
  Inyectables: "gold",
  Bioestimulación: "lilac",
  Faciales: "rose",
  Corporal: "beige",
  Aparatología: "clinic",
  Contorno: "terracotta",
  Bienestar: "clinic",
};

export default function ClinicSection() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const featured = clinicalServices.filter((service) => service.featured).slice(0, 6);
  const active = activeIndex === null ? null : featured[activeIndex];

  return (
    <section id="clinica" className="relative overflow-hidden bg-background-100">
      <div className="pointer-events-none absolute -left-24 top-24 h-[360px] w-[360px] rounded-full glow-rose blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-32 h-[380px] w-[380px] rounded-full glow-clinic blur-3xl" />

      <div className="relative mx-auto w-full max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <Reveal className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <Eyebrow>Unidad 01 · Clínica estética</Eyebrow>
            <h2 className="mt-4 font-heading text-4xl font-bold leading-[1.05] text-foreground-950 md:text-5xl">
              Tratamientos con valoración previa
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-foreground-600">
            Servicios destacados de nuestra clínica. Toca cualquiera para ver beneficios, opciones y
            agendar tu valoración.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((service, index) => {
            const tone = toneStyles[categoryTone[service.category] ?? "gold"];
            return (
              <Reveal key={service.name} delay={(index % 3) * 80} className="relative flex">
                <span
                  aria-hidden
                  className={`pointer-events-none absolute -inset-4 rounded-[40px] opacity-50 blur-2xl ${tone.halo}`}
                />
                <button
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Ver detalle y agendar: ${service.name}`}
                  className={`card-soft group relative flex w-full flex-col overflow-hidden rounded-[24px] border border-secondary-200 bg-background-50 text-left transition-all duration-500 hover:-translate-y-1 ${tone.shadow}`}
                >
                  <span className={`absolute inset-x-0 top-0 z-10 h-[3px] ${tone.bar}`} />

                  <div className="relative h-[280px] w-full overflow-hidden bg-background-100">
                    <img
                      src={service.image}
                      alt={`${service.name} — ALÉA Clínica`}
                      title={`${service.name} tratamiento clínica estética Culiacán`}
                      className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-primary-950/35 to-transparent" />
                    <Badge tone={tone.chip} className="absolute left-3 top-4">
                      {service.category}
                    </Badge>
                    <Badge tone="neutral" icon="ri-time-line" className="absolute right-3 top-4">
                      {service.duration}
                    </Badge>
                    {service.badge ? (
                      <Badge tone="gold" icon="ri-award-fill" className="absolute bottom-3 left-3">
                        {service.badge}
                      </Badge>
                    ) : null}
                  </div>

                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-xl font-bold leading-snug text-foreground-950">
                      {service.name}
                    </h3>
                    <p className="mt-2 flex-1 text-[13px] leading-relaxed text-foreground-600">
                      {service.text}
                    </p>

                    <div className="mt-4 flex items-center justify-between border-t border-secondary-200 pt-4">
                      <span className="font-heading text-lg font-bold text-foreground-950">
                        {service.price}
                      </span>
                      <span className="flex items-center gap-1.5 whitespace-nowrap font-label text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-700 transition-colors group-hover:text-accent-700">
                        Ver detalle
                        <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal
          delay={120}
          className="relative mt-12 flex flex-col items-start justify-between gap-5 overflow-hidden rounded-[24px] bg-primary-500 px-7 py-8 sm:flex-row sm:items-center md:px-10"
        >
          <div className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full glow-rose blur-3xl" />
          <div className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full glow-lilac blur-3xl" />
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bar-gold" />
          <p className="relative max-w-xl font-heading text-2xl font-bold leading-snug text-background-50 md:text-[30px]">
            No vendemos procedimientos.{" "}
            <span className="text-accent-400">Diseñamos resultados</span> que se ven como tú.
          </p>
          <a
            href={buildWhatsAppLink("Hola ALÉA, quiero información sobre tratamientos de clínica.")}
            target="_blank"
            rel="noreferrer"
            className="relative inline-flex min-h-[48px] shrink-0 items-center gap-2 whitespace-nowrap rounded-[16px] border border-accent-400/60 bg-background-50 px-6 font-label text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-950 transition-colors duration-300 hover:bg-background-100"
          >
            Agenda por WhatsApp
            <i className="ri-whatsapp-line text-base" />
          </a>
        </Reveal>
      </div>

      <DetailModal
        open={active !== null}
        onClose={() => setActiveIndex(null)}
        unitLabel={`Unidad 01 · Clínica · ${active?.category ?? ""}`}
        title={active?.name ?? ""}
        image={active?.image ?? ""}
        price={active?.price}
        description={active?.description ?? ""}
        optionsTitle={active?.optionsTitle}
        options={active?.options}
        includesTitle="Incluye"
        includes={active?.benefits}
        ctaLabel="Agendar cita por WhatsApp"
        buildCtaHref={(option?: DetailOption) =>
          buildWhatsAppLink(
            option
              ? `Hola ALÉA, quiero agendar "${active?.name}" — opción: ${option.label} (${option.price}).`
              : `Hola ALÉA, quiero agendar una valoración para ${active?.name}.`
          )
        }
      />
    </section>
  );
}